/** @jsxImportSource @emotion/react */
import { useState, useEffect, useMemo, ChangeEvent, useRef, Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { tb } from 'components/layouts/Table';
import _ from 'lodash';
import { useCommonModal } from 'hooks/useCommonModal';
import { DefaultProps } from 'pages/md/invest/MdIvesCmpuRegPage';
import IvesInqPopup from '../../../popup/IvesInqPopup';
import { st } from 'components/inputs/CustomInput';
import ToggleButton from 'components/buttons/ToggleButton';
import {
  findMdIvesArticleList,
  mergeMdIvesArticle,
  saveMdIvesArticleList,
} from 'apis/md/MdProject';
import { MdIvesArticle, MdIvesMdlSmly } from 'models/md/MdIves';
import UprcInqPopup from 'pages/md/popup/UprcInqPopup';
import { CrudCode } from 'models/common/Edit';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { CommonUtil } from 'utils/CommonUtil';
import { LightBlueColor } from 'ui/theme/Color';
import { resetInvestResult } from 'apis/md/MdInvestResult';
import { findMdIvesAtcRegList } from 'apis/md/MdIvesAtcRegt';
import { MdIvesAtcReg } from 'models/md/MdIvesActReg';
import MultiUprcInqPopup from 'pages/md/popup/MultiUprcInqPopup';

type PopupType = 'INVEST' | 'COST' | null; // 투자항목추가 | 단가 검색

interface IvesAtcIsptProps extends DefaultProps {
  selectedSmlyModel?: MdIvesMdlSmly;
  isInit?: any;
  setIsInit: Dispatch<any>;
  eolTraysPerBox: string | undefined;
}
interface PopupProps {
  isShow: PopupType;
  selectedItem?: any;
}
interface AtcCodesProps extends MdIvesAtcReg {
  cmnCd?: string;
  cmnCdNm?: string;
}

const IvesAtcIspt = ({
  elmPrjId,
  ivesMdlId,
  selectedSmlyModel,
  isInit,
  setIsInit,
  isEditable = true,
  isRegStat,
  eolTraysPerBox = '',
}: IvesAtcIsptProps) => {
  const { t } = useTranslation();
  const { isLoading, openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();

  const [idCnt, setIdCnt] = useState(0);
  const [atcCodes, setAtcCodes] = useState<AtcCodesProps[]>([]);
  const [nestedRowData, setNestedRowData] = useState<any>([]);
  const [activePopup, setActivePopup] = useState<PopupProps>({ isShow: null });

  const layoutDefinition = [
    {
      binding: 'prdnProcCdNm',
      header: String(t('md.label.공정', '공정')),
      width: 120,
    },
    {
      binding: 'eqclNm',
      header: String(t('md.label.설비군', '설비군')),
      width: 170,
    },
    {
      binding: 'ivesAtcNm',
      header: String(t('md.label.투자항목', '투자항목')),
      width: 180,
    },
    {
      binding: 'ivesDtlDesc',
      header: String(t('md.label.투자상세', '투자상세')),
      width: 210,
    },
    {
      binding: 'eqpCnt',
      header: String(t('md.label.수량', '수량')),
      width: 70,
    },
    {
      binding: 'quotUprc',
      header: String(t('md.label.단가', '단가')),
      width: 120,
    },
    {
      binding: 'ivesCost',
      header: String(t('md.label.투자비', '투자비')),
      width: 80,
    },
    {
      binding: 'calLogcCtn',
      header: String(t('md.label.수량 기준', '수량 기준')),
      width: '*',
    },
    {
      binding: 'uprcDcnCtn',
      header: String(t('md.label.단가기준', '단가기준')),
      width: 100,
    },
    {
      binding: 'tgtMdlNm',
      header: String(t('md.label.Ref 모델', 'Ref 모델')),
      width: 110,
    },
    {
      binding: 'wbsCd',
      header: String(t('md.label.WBS', 'WBS')),
      width: 110,
    },
    {
      binding: 'stndYn',
      header: String(t('md.label.비고', '비고')),
      width: 60,
    },
  ];

  const flattedData: MdIvesArticle[] = useMemo(() => {
    return _.flattenDeep(nestedRowData.map((proc) => proc.eqclGroups.map((eqcl) => eqcl.items)));
  }, [nestedRowData]);

  const filteredAtcCodes = useMemo(() => {
    return atcCodes.filter((o) => o.stndYn === 'Y');
  }, [atcCodes]);

  const isModify = () => {
    return !!ivesMdlId && !ivesMdlId.includes('_NEW_') && !!elmPrjId;
  };

  const initCodes = async () => {
    const ATC_LIST = await findMdIvesAtcRegList({});
    setAtcCodes(ATC_LIST.map((o) => ({ ...o, cmnCd: o.ivesAtcId, cmnCdNm: o.ivesAtcNm })));
  };

  const getData = () => {
    if (!elmPrjId || !ivesMdlId || ivesMdlId.includes('_NEW_')) {
      setIdCnt(0);
      setNestedRowData([]);
      return;
    }
    if (!isModify()) return;

    findMdIvesArticleList({ ivesMdlId, elmPrjId }).then((res) => {
      const nestedObj = (res || []).reduce((acc, item: MdIvesArticle) => {
        const prdnProcCd = item.prdnProcCd || 'UNDEFINED';
        const eqclId = item.eqclId || 'UNDEFINED';

        // 1. 공정(Process) 레벨 초기화
        if (!acc[prdnProcCd]) {
          acc[prdnProcCd] = {
            ...item,
            depth: 1,
            isShow: true,
            checked: false,
            eqclGroups: {},
          };
        }
        // 2. 설비군(Equipment Group) 레벨 초기화
        if (!acc[prdnProcCd].eqclGroups[eqclId]) {
          acc[prdnProcCd].eqclGroups[eqclId] = {
            ...item,
            depth: 2,
            isShow: true,
            checked: false,
            items: [],
          };
        }
        // 3. 투자항목 데이터 삽입
        acc[prdnProcCd].eqclGroups[eqclId].items.push({
          ...item,
          checked: false,
        });

        return acc;
      }, {});

      const result = Object.values(nestedObj).map((proc: any) => {
        return {
          ...proc,
          eqclGroups: Object.values(proc.eqclGroups).map((group: any) => {
            return {
              ...group,
              items: group.items.map((o) => ({ ...o, depth: 3 })),
            };
          }),
        };
      });
      setNestedRowData(result);
    });
  };

  const btnAdd = () => {
    if (!ivesMdlId || ivesMdlId.includes('_NEW_')) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.모델을 등록해주세요.', '모델을 등록해주세요.'),
      });
      return;
    }
    if (!selectedSmlyModel?.mdlSmlyId) {
      openMessageBar({
        type: 'error',
        content: t(
          'com.msg.유사모델이 산출되지 않았습니다. 유사모델 산출 후 투자항목을 추가하십시요',
          '유사모델이 산출되지 않았습니다. 유사모델 산출 후 투자항목을 추가하십시요'
        ),
      });
      return;
    }

    setActivePopup({ isShow: 'INVEST' });
  };

  const btnDel = () => {
    const flatted: any = _.flattenDeep(
      nestedRowData.map((proc) => proc.eqclGroups.map((eqcl) => eqcl.items))
    );

    const selected = flatted.filter((o) => o.checked).map((o) => o.mdlIvesAtcId);
    if (!selected?.length) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.선택된 데이터가 없습니다.', '선택된 데이터가 없습니다.'),
      });
      return;
    }

    openCommonModal({
      modalType: 'confirm',
      content: t(
        'com.msg.비표준 항목 삭제 시 상세 점검사항에서도 삭제됩니다.<br/>해당 항목을 삭제하시겠습니까?',
        '비표준 항목 삭제 시 상세 점검사항에서도 삭제됩니다.<br/>해당 항목을 삭제하시겠습니까?'
      ),
      yesCallback: () => {
        const delItems = flatted.map((o) => {
          if (selected.includes(o.mdlIvesAtcId)) {
            return { ...o, crudKey: CrudCode.DELETE };
          } else return o;
        });

        openLoading(true);
        saveMdIvesArticleList(delItems)
          .then((res) => {
            if (res.successOrNot === 'Y') {
              openMessageBar({
                type: 'confirm',
                content: t('com.msg.삭제 되었습니다.', '삭제 되었습니다.'),
              });
              setIsInit((prev) => !prev);

              // 투자항목 결과 분석 초기화
              resetInvestResult(elmPrjId, ivesMdlId);
            } else {
              openMessageBar({
                type: 'error',
                content: t('com.msg.삭제에 실패하였습니다.', '삭제에 실패하였습니다.'),
              });
            }
          })
          .catch((err) => {
            openMessageBar({
              type: 'error',
              content: t('com.msg.삭제 중 오류가 발생했습니다.', '삭제 중 오류가 발생했습니다.'),
            });
          })
          .finally(() => openLoading(false));
      },
    });
  };

  const btnSave = () => {
    openCommonModal({
      modalType: 'confirm',
      content: t(`com.msg.변경사항을 저장하시겠습니까?`, `변경사항을 저장하시겠습니까?`),
      yesCallback: async () => {
        openLoading(true);
        saveMdIvesArticleList(flattedData)
          .then((res) => {
            if (res.successOrNot === 'Y') {
              openMessageBar({
                type: 'confirm',
                content: t('com.msg.저장 되었습니다.', '저장 되었습니다.'),
              });
              setIsInit((prev) => !prev);

              // 투자항목 결과 분석 초기화
              resetInvestResult(elmPrjId, ivesMdlId);
            } else {
              openMessageBar({
                type: 'error',
                content: res.data || t('com.msg.저장에 실패하였습니다.', '저장에 실패하였습니다.'),
              });
            }
          })
          .catch((err) => {
            openMessageBar({
              type: 'error',
              content: t('com.msg.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
          })
          .finally(() => openLoading(false));
      },
    });
  };

  const btnChkAll = (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setNestedRowData((prev) =>
      prev.map((proc) => ({
        ...proc,
        checked,
        eqclGroups: proc.eqclGroups.map((eqcl) => ({
          ...eqcl,
          checked,
          items: eqcl.items.map((o) => ({ ...o, checked })),
        })),
      }))
    );
  };

  const btnChk = (checked: boolean, item: any) => {
    if (item.depth === 1) {
      //
      setNestedRowData((prev) =>
        prev.map((proc) => {
          if (proc.prdnProcCd === item.prdnProcCd) {
            return {
              ...proc,
              checked,
              eqclGroups: proc.eqclGroups.map((eqcl) => ({
                ...eqcl,
                checked,
                items: eqcl.items.map((o) => ({ ...o, checked })),
              })),
            };
          } else return { ...proc };
        })
      );
    } else if (item.depth === 2) {
      setNestedRowData((prev) =>
        prev.map((proc) => {
          if (proc.prdnProcCd === item.prdnProcCd) {
            return {
              ...proc,
              eqclGroups: proc.eqclGroups.map((eqcl) => {
                if (eqcl.eqclId === item.eqclId) {
                  return { ...eqcl, checked, items: eqcl.items.map((o) => ({ ...o, checked })) };
                } else return { ...eqcl };
              }),
            };
          } else return { ...proc };
        })
      );
    } else {
      setNestedRowData((prev) =>
        prev.map((proc) => {
          if (proc.prdnProcCd === item.prdnProcCd) {
            return {
              ...proc,
              eqclGroups: proc.eqclGroups.map((eqcl) => {
                if (eqcl.eqclId === item.eqclId) {
                  return {
                    ...eqcl,
                    items: eqcl.items.map((o) => {
                      if (o.mdlIvesAtcId === item.mdlIvesAtcId) {
                        return { ...o, checked };
                      } else return { ...o };
                    }),
                  };
                } else return { ...eqcl };
              }),
            };
          } else return { ...proc };
        })
      );
    }
  };

  const btnAddRow = (prdnProcCd: string, prdnProcCdNm: string, eqclId: string, eqclNm: string) => {
    setNestedRowData((prev) =>
      prev.map((proc) => {
        if (proc.prdnProcCd === prdnProcCd) {
          return {
            ...proc,
            eqclGroups: proc.eqclGroups.map((eqcl) => {
              if (eqcl.eqclId === eqclId) {
                const item = {
                  elmPrjId,
                  ivesMdlId,
                  prdnProcCd,
                  prdnProcCdNm,
                  eqclId,
                  eqclNm,
                  eqpCnt: null,
                  quotUprc: null,
                  stndYn: 'Y',
                  uprcDcnTpCd: 'NA',
                  crudKey: CrudCode.CREATE,
                  isAddRow: true,
                };
                return {
                  ...eqcl,
                  items: [item, ...eqcl.items],
                };
              } else return { ...eqcl };
            }),
          };
        } else return { ...proc };
      })
    );
  };

  const onToggleRow = (item) => {
    // 공정 Depth인 경우
    if (item.depth === 1) {
      setNestedRowData((prev) =>
        prev.map((o) => {
          return o === item ? { ...o, isShow: !o.isShow } : o;
        })
      );
    }
    // 설비군 Depth인 경우
    else {
      setNestedRowData((prev) =>
        prev.map((o) => ({
          ...o,
          eqclGroups: o.eqclGroups.map((x) => {
            return x === item ? { ...x, isShow: !x.isShow } : x;
          }),
        }))
      );
    }
  };

  ////////////////////////////////////////////////////////
  // 통관료/보험료 등 전체 투자비 비율로 게산하는 항목은 버튼을 다르게 표시하기 위한 함수
  const getBtnImgClass = (uprcDcnTpCd) => {
    // const LIST = ['IA00040', 'IA00041', 'IA00042']; // 전극 운송/통관료, 셀 운송/통관료, 설비 보험료
    return uprcDcnTpCd === 'NA' ? 'refresh' : 'find';
  };

  // 전극 운송/통관료, 셀 운송/통관료, 설비 보험료 단가 계산 함수
  const calcQuotUprc = (list: MdIvesArticle[], ivesAtcId: string) => {
    if (ivesAtcId === 'IA00040') {
      const sumList = list.filter((o) => ['E'].includes(o.prdnProcCd || ''));
      return _.round(_.sumBy(sumList, (o) => Number(o.eqpCnt) * Number(o.quotUprc)) * 0.05, 7);
    } else if (ivesAtcId === 'IA00041') {
      const sumList = list.filter((o) => ['A', 'F'].includes(o.prdnProcCd || ''));
      return _.round(_.sumBy(sumList, (o) => Number(o.eqpCnt) * Number(o.quotUprc)) * 0.05, 7);
    } else if (ivesAtcId === 'IA00042') {
      const sumList = list.filter((o) => ['E', 'A', 'F'].includes(o.prdnProcCd || ''));
      return _.round(_.sumBy(sumList, (o) => Number(o.eqpCnt) * Number(o.quotUprc)) * 0.015, 7);
    } else return;
  };
  ////////////////////////////////////////////////////////

  const findQuotUprc = (item) => {
    if (item.uprcDcnTpCd !== 'NA') {
      setActivePopup({
        isShow: 'COST',
        selectedItem: item,
      });
    } else {
      handleOnChangeObj(item, {
        quotUprc: calcQuotUprc(flattedData, item.ivesAtcId),
        uprcHdfgInsYn: 'N',
        crudKey: CrudCode.UPDATE,
      });
    }
  };

  const getInvestCost = (eqpCnt, quotUprc) => {
    if (!eqpCnt || !quotUprc) {
      return '';
    } else {
      return (Number(eqpCnt) * Number(quotUprc)).toFixed(1);
    }
  };

  const handleOnChange = (item, key, value) => {
    setNestedRowData((prev) =>
      prev.map((prdn) => {
        if (prdn.prdnProcCd === item.prdnProcCd) {
          return {
            ...prdn,
            eqclGroups: prdn.eqclGroups.map((eqcl) => {
              if (eqcl.eqclId === item.eqclId) {
                let updateItems = eqcl.items.map((o) => {
                  if (o.mdlIvesAtcId === item.mdlIvesAtcId) {
                    if (key === 'eqpCnt') {
                      return {
                        ...o,
                        [key]: value,
                        cntHdfgInsYn: 'Y',
                        crudKey: CrudCode.UPDATE,
                      };
                    } else if (key === 'quotUprc') {
                      return {
                        ...o,
                        [key]: value,
                        uprcHdfgInsYn: 'Y',
                        crudKey: CrudCode.UPDATE,
                      };
                    } else {
                      return { ...o, [key]: value, crudKey: CrudCode.UPDATE };
                    }
                  } else return o;
                });

                // 출하 Tray 수량 변경 시 출하 Box 수량 재계산
                if (item.ivesAtcId === 'IA00038' && key === 'eqpCnt') {
                  const trayPerBox = Number(eolTraysPerBox);
                  const eqpCnt =
                    (trayPerBox === 0 ? '' : Math.round(Number(value) / Number(eolTraysPerBox))) ||
                    '';
                  updateItems = updateItems.map((o) =>
                    o.ivesAtcId === 'IA00039'
                      ? {
                          ...o,
                          eqpCnt,
                          cntHdfgInsYn: 'N',
                          crudKey: CrudCode.UPDATE,
                        }
                      : o
                  );
                }

                return {
                  ...eqcl,
                  items: updateItems,
                };
              } else return eqcl;
            }),
          };
        } else return prdn;
      })
    );
  };
  const handleOnChangeObj = (item, obj) => {
    setNestedRowData((prev) =>
      prev.map((prdn) => {
        if (prdn.prdnProcCd === item.prdnProcCd) {
          return {
            ...prdn,
            eqclGroups: prdn.eqclGroups.map((eqcl) => {
              if (eqcl.eqclId === item.eqclId) {
                return {
                  ...eqcl,
                  items: eqcl.items.map((o) => {
                    if (o.mdlIvesAtcId === item.mdlIvesAtcId) {
                      return { ...o, ...obj };
                    } else return o;
                  }),
                };
              } else return eqcl;
            }),
          };
        } else return prdn;
      })
    );
  };
  const handleOnChangeList = (item, list) => {
    /**
     * 투자이력 database에서 단가 조회 후 적용 시 바로 저장 로직을 수행함
     * --> 많은 행의 데이터를 수정 후 저장버튼을 실수로 누르지않고 새로고침등이 될 경우 데이터가 모두 날라가는 현상 방지를 위함
     * 유사모델 합계로 행이 여러개 추가된 경우에는 여러행 데이터 저장
     */
    const addList = list.map((o, i) => ({
      ...item,
      ...o,
      ivesAtcId: item.uprcDcnTpCd !== 'NA' ? item.ivesAtcId : o.ivesAtcId,
      ivesAtcNm: item.uprcDcnTpCd !== 'NA' ? item.ivesAtcNm : o.ivesAtcNm,
      eqpCnt: i === 0 ? item.eqpCnt : '',
      cntHdfgInsYn: i === 0 ? item.cntHdfgInsYn : 'N',
      mdIvesAtcId: i === 0 ? item.mdlIvesAtcId : '_NEW_' + i + '_' + item.mdlIvesAtcId,
      crudKey: !item.isAddRow && i === 0 ? CrudCode.UPDATE : CrudCode.CREATE,
    }));

    openLoading(true);
    saveMdIvesArticleList(addList)
      .then((res) => {
        if (res.successOrNot === 'Y') {
          getData();
        } else {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.투자이력(단가) 데이터 조회 중 오류가 발생하였습니다.',
              '투자이력(단가) 데이터 조회 중 오류가 발생하였습니다.'
            ),
          });
        }
      })
      .catch((err) => {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.데이터 조회 중 오류가 발생하였습니다.',
            '데이터 조회 중 오류가 발생하였습니다.'
          ),
        });
      })
      .finally(() => openLoading(false));
  };

  // 투자비 합계 계산
  const getSumInvestCost = (prdn, eqcl = '') => {
    // 공정별 합계 계산
    if (!eqcl) {
      const sumList = flattedData.filter((o) => o.prdnProcCd === prdn);
      return _.sumBy(sumList, (o) => Number(o.eqpCnt) * Number(o.quotUprc)).toFixed(1);
    }
    // 설비군별 합계 계산
    else {
      const sumList = flattedData.filter((o) => o.prdnProcCd === prdn && o.eqclId === eqcl);
      return _.sumBy(sumList, (o) => Number(o.eqpCnt) * Number(o.quotUprc)).toFixed(1);
    }
  };

  // 투자항목 추가 callback function
  const ivesAtcCallbackFn = (selectedItems: MdIvesArticle[]) => {
    const reqData = _.uniqBy(
      [
        ...flattedData,
        ...selectedItems.map((o: any) => ({
          ...o,
          elmPrjId,
          ivesMdlId,
          crudKey: CrudCode.CREATE,
        })),
      ],
      (o) => (o.prdnProcCd ?? '') + (o.eqclId ?? '') + (o.ivesAtcId ?? '')
    );

    saveAndMergeData(reqData);
  };

  const saveAndMergeData = async (dataList: MdIvesArticle[]) => {
    /**
     * 1. 추가한 투자항목을 저장한다.
     * 2. merge 쿼리를 활용해 유사모델의 투자이력을 매핑한다.
     * 3. reload를 통해 데이터를 다시 조회한다.
     */
    openLoading(true);
    const result = await saveMdIvesArticleList(dataList);
    if (result.successOrNot === 'Y') {
      getData();
    } else {
      openMessageBar({
        type: 'error',
        content: t(
          'com.msg.투자항목 저장 중 오류가 발생하였습니다.',
          '투자항목 저장 중 오류가 발생하였습니다.'
        ),
      });
    }
    openLoading(false);
  };

  const getIvesItems = () => {
    // 상세점검사항 기준으로 팝업을 만들어서
    // 투자항목 사용시 상세점검사항처럼 데이터 매핑 필요
    const filtered = flattedData
      .filter((o) => o.stndYn === 'Y' && o.uprcDcnTpCd !== 'NA')
      .map((o) => ({
        ...o,
        mdlDtlIsptId: o.mdlIvesAtcId, // 투자항목 -> 상세점검사항 데이터 매핑
        dtlIsptId: o.ivesAtcId, // 투자항목 -> 상세점검사항 데이터 매핑
        dtlIsptNm: o.ivesAtcNm, // 투자항목 -> 상세점검사항 데이터 매핑
        selectYn: o.mdRltDtlId ? 'Y' : o.uprcHdfgInsYn === 'Y' ? 'H' : 'N',
      }));

    return _.uniqBy(
      filtered.map((o) => ({
        // 투자비 검색 조건
        stndYn: o.stndYn,
        elmPrjId: o.elmPrjId,
        ivesMdlId: o.ivesMdlId,
        uprcDcnTpCd: o.uprcDcnTpCd,
        ivesAtcNm: o.dtlIsptNm,
        // UI 매핑
        mdlDtlIsptId: o.mdlDtlIsptId,
        prdnProcCd: o.prdnProcCd,
        prdnProcCdNm: o.prdnProcCdNm,
        eqclId: o.eqclId,
        eqclNm: o.eqclNm,
        dtlIsptId: o.dtlIsptId,
        dtlIsptNm: o.dtlIsptNm,
        selectYn: o.selectYn,
        child: filtered
          .filter(
            (x) =>
              x.prdnProcCd === o.prdnProcCd && x.eqclId === o.eqclId && x.ivesAtcId === o.ivesAtcId
          )
          .map((x) => ({ ...x, crudKey: undefined })),
      })),
      (o) => `${o.prdnProcCd}${o.eqclId}${o.dtlIsptId}`
    );
  };

  // 투자항목 Mapping 팝업 callback function
  const multiUprcInqCallbackFn = async (items) => {
    openLoading(true);
    const result = await saveMdIvesArticleList(items);
    if (result.successOrNot === 'Y') {
      getData();

      // 투자항목 결과 분석 초기화
      const r2 = await resetInvestResult(elmPrjId, ivesMdlId);
      if (r2.successOrNot !== 'Y') {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.투자항목 결과 분석 초기화 중 오류가 발생했습니다.',
            '투자항목 결과 분석 초기화 중 오류가 발생했습니다.'
          ),
        });
      } else {
        openMessageBar({
          type: 'confirm',
          content: t('com.msg.저장되었습니다.', '저장되었습니다.'),
        });
      }
    } else {
      openMessageBar({
        type: 'error',
        content: t(
          'com.msg.투자항목 저장 중 오류가 발생하였습니다.',
          '투자항목 저장 중 오류가 발생하였습니다.'
        ),
      });
    }
    setActivePopup({ isShow: null });
    openLoading(false);
  };

  useEffect(() => {
    initCodes();
  }, []);

  useEffect(() => {
    getData();
  }, [ivesMdlId, elmPrjId, isInit]);

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.투자항목 점검', '투자항목 점검')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(flattedData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <GlobalBtnGroup>
          <label className="text-sm mr-2">{t('md.label.단가(억원)', '단가(억원)')}</label>
          {isEditable && (
            <>
              <Button
                css={IconButton.button}
                className="icoNone ok"
                disabled={!isModify() || !isRegStat}
                onClick={btnAdd}
              >
                {t('com.label.투자항목 추가', '투자항목 추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="delRow"
                disabled={!isModify() || !isRegStat}
                onClick={btnDel}
              >
                {t('com.label.행삭제', '행삭제')}
              </Button>
              <Button
                css={IconButton.button}
                className="save"
                disabled={!isModify() || !isRegStat || isLoading}
                onClick={btnSave}
              >
                {t('com.label.투자항목 저장', '투자항목 저장')}
              </Button>
            </>
          )}
        </GlobalBtnGroup>
      </SubTitleLayout>
      <TableContainer css={tb.tableCol}>
        <Table>
          <TableHead>
            <TableRow>
              {isEditable && (
                <TableCell width={43}>
                  <Checkbox css={st.checkbox} className="custom" onChange={btnChkAll} />
                </TableCell>
              )}
              {layoutDefinition.map((item, idx) => {
                return (
                  <TableCell key={idx} width={item.width}>
                    {item.header}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {nestedRowData.map((proc, idx) => {
              return (
                <>
                  <TableRow className="bg-cyan-100">
                    {isEditable && (
                      <TableCell>
                        <Checkbox
                          css={st.checkbox}
                          className="custom"
                          data-id="checkbox"
                          checked={proc.checked}
                          onChange={(e, c) => btnChk(c, proc)}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <ToggleButton
                        text={proc.prdnProcCdNm}
                        initialOpen={proc.isShow}
                        onToggle={() => onToggleRow(proc)}
                      />
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell className="!font-bold">
                      {getSumInvestCost(proc.prdnProcCd, '')}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  {proc.isShow &&
                    proc.eqclGroups.map((eqcl, idx2) => {
                      return (
                        <>
                          <TableRow key={idx2} className="bg-violet-100">
                            {isEditable && (
                              <TableCell>
                                <Checkbox
                                  css={st.checkbox}
                                  className="custom"
                                  data-id="checkbox"
                                  checked={eqcl.checked}
                                  onChange={(e, c) => btnChk(c, eqcl)}
                                />
                              </TableCell>
                            )}
                            <TableCell></TableCell>
                            <TableCell>
                              <div className="flex justify-between items-center">
                                <ToggleButton
                                  text={eqcl.eqclNm}
                                  initialOpen={eqcl.isShow}
                                  onToggle={() => onToggleRow(eqcl)}
                                />
                              </div>
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell className="!font-bold">
                              {getSumInvestCost(proc.prdnProcCd, eqcl.eqclId)}
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                          {eqcl.isShow &&
                            eqcl.items.map((item, idx3) => {
                              return (
                                <TableRow
                                  key={idx3}
                                  className={`${
                                    item.stndYn === 'N' ? 'bg-gray-200' : 'hover:bg-gray-100'
                                  }`}
                                >
                                  {isEditable && (
                                    <TableCell>
                                      <Checkbox
                                        css={st.checkbox}
                                        className="custom"
                                        data-id="checkbox"
                                        checked={item.checked}
                                        onChange={(e, c) => btnChk(c, item)}
                                      />
                                    </TableCell>
                                  )}
                                  <TableCell>{/* 공정 */}</TableCell>
                                  <TableCell>{/* 설비군 */}</TableCell>
                                  <TableCell className="left">{item.ivesAtcNm}</TableCell>
                                  <TableCell className="left">{item.ivesDtlDesc}</TableCell>
                                  <TableCell className="right">
                                    {isEditable ? (
                                      <CustomInputWithSearch
                                        name="eqpCnt"
                                        style={
                                          item.cntHdfgInsYn === 'Y'
                                            ? {
                                                borderColor: LightBlueColor.LightBlue700,
                                                backgroundColor: LightBlueColor.LightBlue100,
                                              }
                                            : {}
                                        }
                                        value={item.eqpCnt}
                                        onChange={(e) =>
                                          handleOnChange(
                                            item,
                                            'eqpCnt',
                                            CommonUtil.onlyNumInput(e.target.value, 0)
                                          )
                                        }
                                      />
                                    ) : (
                                      <span>{item.eqpCnt}</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isEditable && item.stndYn === 'Y' ? (
                                      <CustomInputWithSearch
                                        name="quotUprc"
                                        className={`suffix ${getBtnImgClass(item.uprcDcnTpCd)}`}
                                        style={
                                          item.uprcHdfgInsYn === 'Y'
                                            ? {
                                                borderColor: LightBlueColor.LightBlue700,
                                                backgroundColor: LightBlueColor.LightBlue100,
                                              }
                                            : {}
                                        }
                                        value={item.quotUprc}
                                        autoComplete="off"
                                        onSearchClick={(e) => findQuotUprc(item)}
                                        onChange={(e) =>
                                          handleOnChange(
                                            item,
                                            'quotUprc',
                                            CommonUtil.onlyNumInput(e.target.value, 7)
                                          )
                                        }
                                      />
                                    ) : (
                                      item.quotUprc
                                    )}
                                  </TableCell>
                                  <TableCell>{getInvestCost(item.eqpCnt, item.quotUprc)}</TableCell>
                                  <TableCell>{item.calLogcCtn}</TableCell>
                                  <TableCell>{item.uprcDcnCtn}</TableCell>
                                  <TableCell>{item.tgtMdlNm}</TableCell>
                                  <TableCell>{item.wbsCd}</TableCell>
                                  <TableCell>
                                    {item.stndYn === 'Y'
                                      ? String(t('md.label.표준', '표준'))
                                      : String(t('md.label.비표준', '비표준'))}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </>
                      );
                    })}
                </>
              );
            })}
            {!nestedRowData?.length && (
              <TableRow>
                <TableCell colSpan={layoutDefinition.length}>
                  조회 가능한 데이터가 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {activePopup.isShow === 'INVEST' && ( // 투자항목추가
        <IvesInqPopup
          open={activePopup.isShow === 'INVEST'}
          close={() => setActivePopup({ isShow: null })}
          ids={flattedData?.map((o) => `${o.prdnProcCd}_${o.eqclId}_${o.ivesAtcId}` || '') || []}
          condition={{
            elmPrjId: selectedSmlyModel?.elmPrjId || '',
            ivesMdlId: ivesMdlId,
          }}
          callbackFn={ivesAtcCallbackFn}
        />
      )}
      {activePopup.isShow === 'COST' && ( // 단가 검색
        <MultiUprcInqPopup
          open={activePopup.isShow === 'COST'}
          close={() => setActivePopup({ isShow: null })}
          ivesItems={getIvesItems()}
          selectedItem={activePopup.selectedItem}
          callbackFn={multiUprcInqCallbackFn}
        />
      )}
    </>
  );
};

export default IvesAtcIspt;
